import { CalciteOption } from "@esri/calcite-components-react";
import { Button } from "components/Button";
import { Card, CardHeader } from "components/Card";
import { Input } from "components/Input";
import { Select } from "components/Select";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { useContext, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { AliErrorRecord } from "services/ALIErrorRecord";
import {
  classOfServiceOptions,
  functionOfChangeEditOptions,
  functionOfChangeOptions,
  typeOfServiceOptions,
} from "services/ALIRecord.d";
import RecordService from "services/RecordService";
import { formatPhoneNumber } from "utils/helpers";

// Constants
const recordService = new RecordService("ali/error");

// Data loader function
export const loadRecord = async ({ params }): Promise<AliErrorRecord> => {
  try {
    const res = await recordService.getRecords(
      {
        criterias: [{ field: "a.idx", type: "=", value: params.idx }],
      },
      "searchByCriteria",
    );

    if (res.status === 401) {
      throw new Error("Unable to fetch error record");
    }

    if (!res.data?.message?.list?.length) {
      throw new Error("Error record not found");
    }

    const record = res.data.message.list[0];
    const errorDescription =
      await recordService.getErrorDescriptionByErrorNumber(record.error);

    if (errorDescription) {
      record.errorDescription = errorDescription;
    }

    return record;
  } catch (error: Error | any) {
    throw new Error(error.message || "Failed to load error record");
  }
};

// Main component
export default function ALIErrorEdit() {
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const record = useLoaderData() as AliErrorRecord;

  // State management
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean | undefined>(undefined);

  // Initialize form data
  const initialFOC =
    record && functionOfChangeEditOptions.length
      ? (functionOfChangeEditOptions.find((o) => o.value === record.FOC)
          ?.value ?? functionOfChangeEditOptions[0].value)
      : record?.FOC;

  const [aliErrorRequest, setAliErrorRequest] = useState<AliErrorRecord>({
    msag_id: record.msag_id || null,
    orig_msag_id: record.orig_msag_id || null,
    sop_id: record.sop_id || 0,
    FOC: String(initialFOC ?? record.FOC ?? ""),
    CPN: record.CPN || "",
    HNO: record.HNO || "",
    HNS: record.HNS || "",
    PRD: record.PRD || "",
    STN: record.STN || "",
    STS: record.STS || "",
    POD: record.POD || "",
    MCN: record.MCN || "",
    STA: record.STA || "",
    LOC: record.LOC || "",
    NAM: record.NAM || "",
    CLS: record.CLS || "",
    TYS: record.TYS || "",
    Exchange: record.Exchange || "",
    lecESN: record.lecESN || "",
    MainNPA: record.MainNPA || "",
    MainNumber: record.MainNumber || "",
    OrderNumber: record.OrderNumber || "",
    ExtractDate: record.ExtractDate || "",
    lecCOI: record.lecCOI || "",
    CPF: record.CPF || "",
    SourceID: record.SourceID || "",
    lecZipCode: record.lecZipCode || "",
    lecZipExtension: record.lecZipExtension || "",
    GeneralUse: record.GeneralUse || "",
    CustomerCode: record.CustomerCode || "",
    Comments: record.Comments || "",
    lecLON: record.lecLON || "",
    lecLAT: record.lecLAT || "",
    lecELV: record.lecELV || "",
    CellID: record.CellID || "",
    SectorID: record.SectorID || "",
    TARCode: record.TARCode || "",
    Reserved1: record.Reserved1 || "",
    AltNumber: record.AltNumber || "",
    ExpandedExtractDate: record.ExpandedExtractDate || "",
    NENAReserved: record.NENAReserved || "",
    DataProviderID: record.DataProviderID || "",
    Reserved2: record.Reserved2 || "",
    created: record.created || "",
    created_by: record.created_by || "",
    last_modified: record.last_modified || "",
    modified_by: record.modified_by || "",
    web_login: record.web_login || "",
    ali_tmp_idx: record.ali_tmp_idx || null,
    job_id: record.job_id || null,
    error: record.error || 0,
    was_in_ali: record.was_in_ali || "",
    idx: record.idx || 0,
  });

  // Event handlers
  const handleChange = (e: any) => {
    const { name, value } = e.target as
      | HTMLCalciteInputElement
      | HTMLCalciteTextAreaElement;
    setAliErrorRequest({
      ...aliErrorRequest,
      [name]: value,
    });
  };

  const updateAliError = async () => {
    try {
      setIsSaving(true);
      const res = await recordService.updateAliRErrorRecord(aliErrorRequest);
      if (res.status === 200) {
        addNotification({
          title: "Success!",
          message: res.data.message.message,
          type: NOTIFICATION_TYPE.SUCCESS,
        });
        setIsSaving(undefined);
        setTimeout(() => {
          navigate("/ali-errors");
        }, 1000);
      }
    } catch (error: any) {
      addNotification({
        title: "Uh oh.",
        message: "Something went wrong: " + error.message,
        type: NOTIFICATION_TYPE.ERROR,
      });
      setIsSaving(undefined);
    }
  };

  const toggleAdditionalInfo = () => {
    setShowAdditionalInfo((prev) => {
      const next = !prev;
      if (!prev) {
        setTimeout(() => {
          const exchangeInput = document.querySelector(
            'calcite-input[name="exchange"]',
          ) as any | null;
          exchangeInput?.setFocus();
        }, 100);
      }
      return next;
    });
  };

  // Loading state
  if (!record) {
    return <div>Loading...</div>;
  }

  // Render helpers
  const renderErrorSection = () => (
    <Card
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        borderRadius: "8px",
        padding: "12px",
        gridColumn: "1 / span 2",
        border: "1px solid #ddd",
      }}
    >
      <CardHeader
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #ddd",
          padding: "8px 12px",
        }}
      >
        Error List
      </CardHeader>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          alignItems: "center",
        }}
      >
        <Input
          name="error"
          label="Error Number"
          value={record.error?.toString() || ""}
          readonly={true}
          scale="m"
          labelSize="m"
        />
        <Input
          name="errorDescription"
          label="Error Description"
          value={record.errorDescription || ""}
          readonly
          scale="m"
          labelSize="m"
        />
      </div>
    </Card>
  );

  const renderBasicFields = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Input
        name="cpn"
        label="Telephone Number"
        value={formatPhoneNumber(record.CPN) || ""}
        disabled
        scale="s"
        labelSize="s"
      />
      <Input
        name="NAM"
        label="Customer Name"
        value={aliErrorRequest.NAM ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="STA"
        label="State/Province"
        value={aliErrorRequest.STA ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="MCN"
        label="MSAG Community Name"
        value={aliErrorRequest.MCN ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="STN"
        label="Street Name"
        value={aliErrorRequest.STN ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="STS"
        label="Street Suffix"
        value={aliErrorRequest.STS ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="PRD"
        label="Prefix Directional"
        value={aliErrorRequest.PRD ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="POD"
        label="Post Directional"
        value={aliErrorRequest.POD ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="HNO"
        label="House Number"
        value={aliErrorRequest.HNO ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="HNS"
        label="House Number Suffix"
        value={aliErrorRequest.HNS ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="LOC"
        label="Location"
        value={aliErrorRequest.LOC ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecCOI"
        label="County ID"
        value={aliErrorRequest.lecCOI ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
    </div>
  );

  const renderServiceFields = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Select
        label="Function of Change*"
        value={aliErrorRequest.FOC ?? ""}
        name="FOC"
        onChange={handleChange}
        scale="s"
        labelSize="s"
        message={
          "Current Value: " +
          (functionOfChangeOptions.find((option) => option.value === record.FOC)
            ?.label || record.FOC)
        }
      >
        {functionOfChangeEditOptions.map((option) => (
          <CalciteOption key={option.value} value={option.value}>
            {option.label}
          </CalciteOption>
        ))}
      </Select>
      <Input
        name="CPF"
        label="NENA ID"
        value={aliErrorRequest.CPF ?? record.CPF ?? ""}
        scale="s"
        labelSize="s"
        readonly
      />
      <Select
        label="Class of Service*"
        value={aliErrorRequest.CLS ?? ""}
        name="classOfService"
        onChange={(e) =>
          setAliErrorRequest((prev) => ({
            ...prev,
            CLS: e.target.value,
          }))
        }
        scale="s"
        labelSize="s"
      >
        {classOfServiceOptions.map((option) => (
          <CalciteOption key={option.value} value={option.value}>
            {option.label}
          </CalciteOption>
        ))}
      </Select>
      <Select
        label="Type of Service*"
        value={aliErrorRequest.TYS ?? ""}
        name="typeOfService"
        onChange={(e) =>
          setAliErrorRequest((prev) => ({
            ...prev,
            TYS: e.target.value,
          }))
        }
        scale="s"
        labelSize="s"
      >
        {typeOfServiceOptions.map((option) => (
          <CalciteOption key={option.value} value={option.value}>
            {option.label}
          </CalciteOption>
        ))}
      </Select>
      <Input
        name="comments"
        label="Comments"
        value={aliErrorRequest.Comments ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecZipCode"
        label="Zip Code"
        value={aliErrorRequest.lecZipCode ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecZipExtension"
        label="Zip Extension"
        value={aliErrorRequest.lecZipExtension ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="areaCode"
        label="Area Code of Main Number"
        value={aliErrorRequest.MainNPA ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="mainNumber"
        label="Main Number"
        value={aliErrorRequest.MainNumber ?? ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="extractDate"
        label="Extract Date"
        value={record.ExtractDate ?? ""}
        scale="s"
        labelSize="s"
        disabled
      />
    </div>
  );

  const renderAdditionalInfoLeft = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Input
        name="exchange"
        label="Exchange"
        value={aliErrorRequest.Exchange || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecESN"
        label="Emergency Service Number"
        value={aliErrorRequest.lecESN || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="orderNumber"
        label="Order Number"
        value={aliErrorRequest.OrderNumber || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="generalUse"
        label="General Use"
        value={aliErrorRequest.GeneralUse || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecLat"
        label="Latitude"
        value={aliErrorRequest.lecLAT || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecLong"
        label="Longitude"
        value={aliErrorRequest.lecLON || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lecELV"
        label="Elevation"
        value={aliErrorRequest.lecELV || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="nenaReserved"
        label="NENA Reserved"
        value={aliErrorRequest.NENAReserved || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="dataProviderId"
        label="Data Provider ID"
        value={aliErrorRequest.DataProviderID || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="reserved2"
        label="Reserved 2"
        value={aliErrorRequest.Reserved2 || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="customerCode"
        label="Customer Code"
        value={aliErrorRequest.CustomerCode || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
    </div>
  );

  const renderAdditionalInfoRight = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Input
        name="file"
        label="File"
        value={aliErrorRequest.file || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="aliTmpIdx"
        label="ALI Temp Index"
        value={aliErrorRequest.ali_tmp_idx || ""}
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="processed"
        label="Processed"
        value={aliErrorRequest.processed || ""}
        disabled
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="created"
        label="Created"
        value={aliErrorRequest.created || ""}
        disabled
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="lastModified"
        label="Last Modified"
        value={aliErrorRequest.last_modified || ""}
        disabled
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
      <Input
        name="modifiedBy"
        label="Modified By"
        value={aliErrorRequest.modified_by || ""}
        disabled
        scale="s"
        labelSize="s"
        onChange={handleChange}
      />
    </div>
  );

  const renderFormActions = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "20px",
        gap: "10px",
      }}
    >
      <Button
        variant="outline"
        kind="brand"
        label="Close"
        onClick={() => navigate(-1)}
      >
        Close
      </Button>
      <Button
        isLoading={isSaving}
        disabled={isSaving}
        form="aliRecord"
        type="submit"
        width="auto"
      >
        Save
      </Button>
    </div>
  );

  // Main render
  return (
    <div>
      {renderErrorSection()}

      <Card style={{ width: "100%", marginBottom: "20px" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateAliError();
          }}
          id="aliRecord"
        >
          <div style={{ padding: "20px", paddingBottom: "40px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "25px",
              }}
            >
              {renderBasicFields()}
              {renderServiceFields()}
            </div>

            <hr style={{ margin: "20px 0" }} />

            <div style={{ padding: "0 20px" }}>
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                {showAdditionalInfo && (
                  <h3 style={{ marginBottom: "10px" }}>
                    Additional Information
                  </h3>
                )}
              </div>
              <div
                style={{
                  overflow: "hidden",
                  transition:
                    "max-height 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.5s",
                  maxHeight: showAdditionalInfo ? 1000 : 0,
                  opacity: showAdditionalInfo ? 1 : 0,
                }}
              >
                {showAdditionalInfo && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "100px",
                    }}
                  >
                    {renderAdditionalInfoLeft()}
                    {renderAdditionalInfoRight()}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: showAdditionalInfo ? "20px" : "0",
              }}
            >
              <Button
                variant="outline"
                kind="brand"
                onClick={toggleAdditionalInfo}
              >
                {showAdditionalInfo ? "Hide" : "Show"} Additional Information
              </Button>
            </div>

            {renderFormActions()}
          </div>
        </form>
      </Card>
    </div>
  );
}
